import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Shield, Plus, Trash2, Ticket, Coins, Gavel, Check, X, Copy, Send, Users, Pencil, UserMinus, UserPlus } from "lucide-react";

const DEFAULT_IMG = "https://images.unsplash.com/photo-1665827491450-c6f329f2285c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxmYW1pbHklMjBtb3ZpZSUyMG5pZ2h0fGVufDB8fHx8MTc4Nzk0NTI2OHww&ixlib=rb-4.1.0&q=85";

export default function AdminPanel() {
  const [tab, setTab] = useState("events");
  return (
    <Layout>
      <div className="flex items-center gap-3 mb-6">
        <Shield strokeWidth={3} className="w-7 h-7"/>
        <h1 className="font-fredoka text-4xl font-bold">Admin</h1>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ["events", "Events"],
          ["auctions", "Auctions"],
          ["invites", "Invites"],
          ["points", "Award points"],
          ["members", "Members"],
        ].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`}
            onClick={() => setTab(k)}
            className={tab === k ? "brut-btn" : "brut-btn-white"}>
            {l}
          </button>
        ))}
      </div>
      {tab === "events" && <EventsAdmin/>}
      {tab === "auctions" && <AuctionsAdmin/>}
      {tab === "invites" && <InvitesAdmin/>}
      {tab === "points" && <PointsAdmin/>}
      {tab === "members" && <MembersAdmin/>}
    </Layout>
  );
}

function EventsAdmin() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", image_url: DEFAULT_IMG, reward_points: 20 });
  const load = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title required");
    try {
      await api.post("/events", form);
      toast.success("Event created");
      setForm({ title: "", description: "", image_url: DEFAULT_IMG, reward_points: 20 });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const setOutcome = async (event_id, outcome) => {
    try {
      await api.patch(`/events/${event_id}`, { outcome });
      toast.success(outcome === "successful" ? "Marked successful & awarded organizer" : "Updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (event_id) => {
    if (!window.confirm("Delete this event and its auctions?")) return;
    await api.delete(`/events/${event_id}`);
    load();
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={create} className="brut-card p-5 bg-white space-y-3">
        <div className="font-fredoka text-xl font-bold flex items-center gap-2"><Plus strokeWidth={3} className="w-5 h-5"/>New event</div>
        <input data-testid="event-title-input" className="brut-input w-full" placeholder="Movie Night" value={form.title} onChange={e => setForm({...form, title: e.target.value})}/>
        <textarea data-testid="event-description-input" className="brut-input w-full min-h-[80px]" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/>
        <input data-testid="event-image-input" className="brut-input w-full" placeholder="Image URL" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})}/>
        <label className="block text-sm">
          Reward points on success
          <input data-testid="event-reward-input" type="number" min="0" className="brut-input w-full mt-1" value={form.reward_points} onChange={e => setForm({...form, reward_points: parseInt(e.target.value || "0", 10)})}/>
        </label>
        <button data-testid="event-create-btn" className="brut-btn w-full">Create event</button>
      </form>
      <div>
        <div className="font-fredoka text-xl font-bold mb-3">All events</div>
        <div className="grid gap-3">
          {events.map(ev => (
            <div key={ev.event_id} data-testid={`event-row-${ev.event_id}`} className="brut-card p-4 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-fredoka font-bold">{ev.title}</div>
                  <div className="text-xs text-gray-700">Reward: {ev.reward_points} pts • Outcome: {ev.outcome}</div>
                </div>
                <button onClick={() => del(ev.event_id)} data-testid={`event-delete-${ev.event_id}`} className="brut-btn-danger px-2 py-1 text-xs"><Trash2 strokeWidth={3} className="w-4 h-4"/></button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button data-testid={`event-success-${ev.event_id}`} onClick={() => setOutcome(ev.event_id, "successful")} className="brut-btn-mint px-3 py-1 text-xs"><Check strokeWidth={3} className="w-4 h-4"/>Success</button>
                <button data-testid={`event-fail-${ev.event_id}`} onClick={() => setOutcome(ev.event_id, "unsuccessful")} className="brut-btn-white px-3 py-1 text-xs"><X strokeWidth={3} className="w-4 h-4"/>Fizzled</button>
                <button onClick={() => setOutcome(ev.event_id, "pending")} className="brut-btn-white px-3 py-1 text-xs">Reset</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuctionsAdmin() {
  const [events, setEvents] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [form, setForm] = useState({ event_id: "", title: "", description: "", closes_at: defaultCloseAt() });
  function defaultCloseAt() {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
  }
  const load = async () => {
    const [e, a] = await Promise.all([api.get("/events"), api.get("/auctions")]);
    setEvents(e.data); setAuctions(a.data);
    if (!form.event_id && e.data[0]) setForm(f => ({ ...f, event_id: e.data[0].event_id }));
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.event_id) return toast.error("Pick an event");
    if (!form.title.trim()) return toast.error("Title required");
    try {
      const payload = { ...form, closes_at: new Date(form.closes_at).toISOString() };
      await api.post("/auctions", payload);
      toast.success("Auction created");
      setForm({ ...form, title: "", description: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const forceClose = async (id) => {
    if (!window.confirm("Close this auction now?")) return;
    await api.post(`/auctions/${id}/close`);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete auction?")) return;
    await api.delete(`/auctions/${id}`);
    load();
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={create} className="brut-card p-5 bg-white space-y-3">
        <div className="font-fredoka text-xl font-bold flex items-center gap-2"><Gavel strokeWidth={3} className="w-5 h-5"/>New auction</div>
        <label className="block text-sm">Event
          <select data-testid="auction-event-select" className="brut-input w-full mt-1 bg-white" value={form.event_id} onChange={e => setForm({...form, event_id: e.target.value})}>
            <option value="">— pick an event —</option>
            {events.map(ev => <option key={ev.event_id} value={ev.event_id}>{ev.title}</option>)}
          </select>
        </label>
        <input data-testid="auction-title-input" className="brut-input w-full" placeholder="Auction title (e.g. Pick this Friday's movie)" value={form.title} onChange={e => setForm({...form, title: e.target.value})}/>
        <textarea data-testid="auction-description-input" className="brut-input w-full" placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/>
        <label className="block text-sm">Closes at
          <input data-testid="auction-closes-input" type="datetime-local" className="brut-input w-full mt-1" value={form.closes_at} onChange={e => setForm({...form, closes_at: e.target.value})}/>
        </label>
        <button data-testid="auction-create-btn" className="brut-btn w-full">Create auction</button>
      </form>
      <div>
        <div className="font-fredoka text-xl font-bold mb-3">All auctions</div>
        <div className="grid gap-3">
          {auctions.map(a => (
            <div key={a.auction_id} className="brut-card p-4 bg-white" data-testid={`admin-auction-${a.auction_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-fredoka font-bold">{a.title}</div>
                  <div className="text-xs text-gray-700">Status: {a.status} • closes {new Date(a.closes_at).toLocaleString()}</div>
                  {a.status === "closed" && a.winner_user_id && (
                    <div className="text-xs">Winner paid {a.winning_price} pts (top bid {a.top_bid})</div>
                  )}
                </div>
                <div className="flex gap-2">
                  {a.status === "open" && (
                    <button data-testid={`auction-close-${a.auction_id}`} onClick={() => forceClose(a.auction_id)} className="brut-btn-peach px-2 py-1 text-xs">Close now</button>
                  )}
                  <button data-testid={`auction-delete-${a.auction_id}`} onClick={() => del(a.auction_id)} className="brut-btn-danger px-2 py-1 text-xs"><Trash2 strokeWidth={3} className="w-4 h-4"/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InvitesAdmin() {
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ code: "", initial_points: 100, uses_left: 1, makes_admin: false, send_to_email: "", recipient_name: "" });
  const load = async () => {
    const { data } = await api.get("/admin/invites");
    setInvites(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.send_to_email) delete payload.send_to_email;
      if (!payload.recipient_name) delete payload.recipient_name;
      const { data } = await api.post("/admin/invites", payload);
      if (data?.email_sent) toast.success(`Invite emailed to ${form.send_to_email}`);
      else if (form.send_to_email) toast.warning("Invite created, but email failed to send. Copy the code manually.");
      else toast.success("Invite created");
      setForm({ code: "", initial_points: 100, uses_left: 1, makes_admin: false, send_to_email: "", recipient_name: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={create} className="brut-card p-5 bg-white space-y-3">
        <div className="font-fredoka text-xl font-bold flex items-center gap-2"><Ticket strokeWidth={3} className="w-5 h-5"/>New invite</div>
        <input data-testid="invite-code-create-input" className="brut-input w-full uppercase tracking-wider" placeholder="Custom code (optional)" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})}/>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm">Initial points
            <input data-testid="invite-points-input" type="number" min="0" className="brut-input w-full mt-1" value={form.initial_points} onChange={e => setForm({...form, initial_points: parseInt(e.target.value || "0", 10)})}/>
          </label>
          <label className="block text-sm">Uses
            <input data-testid="invite-uses-input" type="number" min="1" className="brut-input w-full mt-1" value={form.uses_left} onChange={e => setForm({...form, uses_left: parseInt(e.target.value || "1", 10)})}/>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm font-bold">
          <input data-testid="invite-admin-checkbox" type="checkbox" className="w-5 h-5 border-2 border-black" checked={form.makes_admin} onChange={e => setForm({...form, makes_admin: e.target.checked})}/>
          Grant admin on redeem
        </label>
        <div className="border-t-2 border-dashed border-black/40 pt-3">
          <div className="font-fredoka font-bold text-sm mb-2 flex items-center gap-2"><Send strokeWidth={3} className="w-4 h-4"/>Send by email (optional)</div>
          <input data-testid="invite-name-input" className="brut-input w-full" placeholder="Recipient name (optional)" value={form.recipient_name} onChange={e => setForm({...form, recipient_name: e.target.value})}/>
          <input data-testid="invite-email-input" type="email" className="brut-input w-full mt-2" placeholder="cousin@example.com" value={form.send_to_email} onChange={e => setForm({...form, send_to_email: e.target.value})}/>
        </div>
        <button data-testid="invite-create-btn" className="brut-btn w-full">
          {form.send_to_email ? "Create & email invite" : "Create invite"}
        </button>
      </form>
      <div>
        <div className="font-fredoka text-xl font-bold mb-3">Existing invites</div>
        <div className="grid gap-3">
          {invites.map(inv => (
            <div key={inv.code} className="brut-card p-4 bg-white flex items-center justify-between" data-testid={`invite-row-${inv.code}`}>
              <div>
                <div className="font-fredoka font-bold text-lg tracking-wider">{inv.code}</div>
                <div className="text-xs text-gray-700">
                  {inv.initial_points} pts • {inv.uses_left} uses left {inv.makes_admin ? "• admin" : ""}
                </div>
              </div>
              <button data-testid={`invite-copy-${inv.code}`} onClick={() => copy(inv.code)} className="brut-btn-white px-3 py-1 text-xs"><Copy strokeWidth={3} className="w-4 h-4"/>Copy</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PointsAdmin() {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({ user_id: "", amount: 10, reason: "" });
  const load = async () => {
    const { data } = await api.get("/users");
    setMembers(data);
    if (!form.user_id && data[0]) setForm(f => ({...f, user_id: data[0].user_id}));
  };
  useEffect(() => { load(); }, []);

  const award = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/points/award", form);
      toast.success("Points awarded");
      setForm({ ...form, amount: 10, reason: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <form onSubmit={award} className="brut-card p-5 bg-white space-y-3">
        <div className="font-fredoka text-xl font-bold flex items-center gap-2"><Coins strokeWidth={3} className="w-5 h-5"/>Award points</div>
        <label className="block text-sm">Member
          <select data-testid="points-user-select" className="brut-input w-full mt-1 bg-white" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})}>
            {members.map(m => <option key={m.user_id} value={m.user_id}>{m.name} ({m.points} pts)</option>)}
          </select>
        </label>
        <label className="block text-sm">Amount (negative to deduct)
          <input data-testid="points-amount-input" type="number" className="brut-input w-full mt-1" value={form.amount} onChange={e => setForm({...form, amount: parseInt(e.target.value || "0", 10)})}/>
        </label>
        <input data-testid="points-reason-input" className="brut-input w-full" placeholder="Reason (optional)" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}/>
        <button data-testid="points-award-btn" className="brut-btn w-full">Award</button>
      </form>
      <div>
        <div className="font-fredoka text-xl font-bold mb-3">Members</div>
        <div className="grid gap-3">
          {members.map(m => (
            <div key={m.user_id} className="brut-card p-4 bg-white flex items-center justify-between">
              <div className="font-bold">{m.name} {m.is_admin && <span className="chip bg-peach ml-2">admin</span>}</div>
              <div className="font-fredoka text-xl font-bold">{m.points}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function MembersAdmin() {
  const [members, setMembers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/members");
    setMembers(data);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (m) => { setEditingId(m.user_id); setEditName(m.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };
  const saveName = async (user_id) => {
    if (!editName.trim()) return toast.error("Name required");
    try {
      await api.patch(`/admin/users/${user_id}`, { name: editName.trim() });
      toast.success("Name updated");
      cancelEdit();
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const toggleAdmin = async (m) => {
    try {
      await api.patch(`/admin/users/${m.user_id}`, { is_admin: !m.is_admin });
      toast.success(m.is_admin ? "Admin role removed" : "Promoted to admin");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const revoke = async (m) => {
    if (!window.confirm(`Remove ${m.name} from the family? Their history stays but they can't sign in until restored.`)) return;
    try {
      await api.post(`/admin/users/${m.user_id}/revoke`);
      toast.success("Member removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const restore = async (m) => {
    try {
      await api.post(`/admin/users/${m.user_id}/restore`);
      toast.success("Member restored");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const deleteUser = async (m) => {
    if (!window.confirm(`Permanently delete ${m.name}? This removes their bids, notifications and history. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${m.user_id}`);
      toast.success("Member deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Users strokeWidth={3} className="w-5 h-5"/>
        <h2 className="font-fredoka text-xl font-bold">Family members</h2>
      </div>
      <div className="grid gap-3">
        {members.map(m => (
          <div key={m.user_id} data-testid={`admin-member-${m.user_id}`}
            className={`brut-card p-4 ${m.is_member ? "bg-white" : "bg-cream"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                {m.picture ? (
                  <img src={m.picture} alt="" className="w-10 h-10 rounded-full border-2 border-black object-cover"/>
                ) : (
                  <div className="w-10 h-10 rounded-full border-2 border-black bg-lilac"/>
                )}
                <div className="min-w-0">
                  {editingId === m.user_id ? (
                    <div className="flex gap-2">
                      <input
                        data-testid={`edit-name-input-${m.user_id}`}
                        className="brut-input py-1 px-2 text-sm"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                      />
                      <button data-testid={`edit-save-${m.user_id}`} onClick={() => saveName(m.user_id)} className="brut-btn-mint px-2 py-1 text-xs"><Check strokeWidth={3} className="w-4 h-4"/></button>
                      <button onClick={cancelEdit} className="brut-btn-white px-2 py-1 text-xs"><X strokeWidth={3} className="w-4 h-4"/></button>
                    </div>
                  ) : (
                    <div className="font-fredoka font-bold text-lg truncate">{m.name}</div>
                  )}
                  <div className="text-xs text-gray-700 truncate">
                    {m.email}
                    {m.is_admin && <span className="chip bg-peach ml-2 py-0 text-[10px]">admin</span>}
                    {!m.is_member && <span className="chip bg-red-200 ml-2 py-0 text-[10px]">removed</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="chip bg-sunny"><Coins strokeWidth={3} className="w-3.5 h-3.5"/>{m.points} pts</div>
                {editingId !== m.user_id && (
                  <button data-testid={`edit-name-btn-${m.user_id}`} onClick={() => startEdit(m)} className="brut-btn-white px-2 py-1 text-xs" title="Edit name"><Pencil strokeWidth={3} className="w-4 h-4"/></button>
                )}
                <button data-testid={`toggle-admin-${m.user_id}`} onClick={() => toggleAdmin(m)}
                  className={`${m.is_admin ? "brut-btn-white" : "brut-btn-peach"} px-2 py-1 text-xs`}
                  title={m.is_admin ? "Remove admin" : "Make admin"}>
                  <Shield strokeWidth={3} className="w-4 h-4"/>
                </button>
                {m.is_member ? (
                  <button data-testid={`revoke-${m.user_id}`} onClick={() => revoke(m)} className="brut-btn-white px-2 py-1 text-xs" title="Remove from family">
                    <UserMinus strokeWidth={3} className="w-4 h-4"/>
                  </button>
                ) : (
                  <button data-testid={`restore-${m.user_id}`} onClick={() => restore(m)} className="brut-btn-mint px-2 py-1 text-xs" title="Restore">
                    <UserPlus strokeWidth={3} className="w-4 h-4"/>
                  </button>
                )}
                <button data-testid={`delete-user-${m.user_id}`} onClick={() => deleteUser(m)} className="brut-btn-danger px-2 py-1 text-xs" title="Delete permanently">
                  <Trash2 strokeWidth={3} className="w-4 h-4"/>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
